import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_VisibilityState,
} from "material-react-table";

import { ProspectTableFieldDefinition } from "../prospect-list/model";
import {
  DEFAULT_SHOW_COLUMN_FILTERS,
  DEFAULT_SHOW_GLOBAL_FILTER,
  DEFAULT_SORTING,
} from "../prospect-list/tableConfig";
import {
  FILTER_PREFIX,
  buildNextParams,
  extractViewStateParams,
  formatFilterValue,
  formatSortParam,
  hasAnyViewStateParams,
  hiddenColumnIds,
  knownFilterKeys,
  mergeServerParamsIntoUrl,
  parseSortParam,
  readColumnFilters,
  readColumnVisibility,
  readPagination,
  readShowColumnFilters,
  readShowGlobalFilter,
  writePageIndex,
  writeReadableParams,
} from "../prospect-list/viewStateUrlCodec";
import { useStaffViewStateSync } from "../../../hooks/useStaffViewStateSync";
import { useReconciledColumnOrder } from "./useReconciledColumnOrder";

export { DEFAULT_SORTING };

/**
 * Shape persisted under `/v3/staff/view-states/prospects`.
 *
 * - `params` — recognised URL view-state keys (`q`, `sort`, `hide`,
 *   `filters`, `search`, and any `filter.*`). Hydrated into the URL on
 *   load when the URL is empty, so the next render reads the same state
 *   the URL would have produced.
 * - `columnOrder` — drag-reorder result. Lives outside the URL because
 *   the v2 codec deliberately keeps it out, and is hydrated into
 *   in-memory state instead.
 */
type ProspectViewStatePayload = {
  params: Record<string, string>;
  columnOrder?: string[];
};

const PROSPECT_VIEW_STATE_NAME = "prospects";
const PROSPECT_VIEW_STATE_VERSION = 1;

type Updater<T> = T | ((old: T) => T);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === "string");

export interface ProspectFilterParams {
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: (updater: Updater<MRT_ColumnFiltersState>) => void;
  globalFilter: string;
  setGlobalFilter: (updater: Updater<string | undefined>) => void;
  resetFiltersAndColumns: () => void;
  columnVisibility: MRT_VisibilityState;
  setColumnVisibility: (updater: Updater<MRT_VisibilityState>) => void;
  columnOrder: string[];
  setColumnOrder: (updater: Updater<string[]>) => void;
  showColumnFilters: boolean;
  setShowColumnFilters: (updater: Updater<boolean>) => void;
  showGlobalFilter: boolean;
  setShowGlobalFilter: (updater: Updater<boolean>) => void;
  sorting: MRT_SortingState;
  setSorting: (updater: Updater<MRT_SortingState>) => void;
  pagination: MRT_PaginationState;
  setPagination: (updater: Updater<MRT_PaginationState>) => void;
  /**
   * `true` once the server view-state GET has resolved (success, 404,
   * error, or version mismatch). Consumers can fold this into a UI
   * loading state to avoid a flash of default values before saved
   * filters / sort / column visibility have had a chance to hydrate.
   */
  hasSettled: boolean;
}

function applyUpdater<T>(updater: Updater<T>, current: T): T {
  if (typeof updater === "function") {
    return (updater as (old: T) => T)(current);
  }
  return updater;
}

/**
 * Owns the prospect list's view state — column filters,
 * global search, sort, column visibility, column order, and the
 * filters/search panel toggles — and keeps three layers in sync:
 *
 * 1. **URL search params** (source of truth for shareable links). Every
 *    setter mutates the URL via `navigate(..., { replace: true })`.
 * 2. **In-memory column order** (kept outside the URL on purpose; see
 *    `viewStateUrlCodec`).
 * 3. **Server-side view state** under
 *    `/v3/staff/view-states/prospects`, persisted via
 *    {@link useStaffViewStateSync}. This makes the staff member's view
 *    follow them across devices.
 *
 * ### Hydration
 *
 * `useStaffViewStateSync` runs a small state machine
 * (`unauthenticated → hydrating → operating`) and exposes two callbacks
 * for the consumer to participate in the lifecycle:
 *
 * - `onHydrate(state)` — fires only on a version-match GET. Updates the
 *   in-memory column order and merges any saved URL keys that weren't
 *   present at mount time. The `mountUrlKeysRef` snapshot prevents
 *   hydration from clobbering keys the user typed before the GET
 *   resolved.
 * - `onSettled` — fires once after the GET resolves (success, 404,
 *   error, version mismatch). If the URL had any view-state at mount
 *   (see `mountSearchParamsRef`), this is where we push the current
 *   merged state to the server so shared/bookmarked links stay
 *   authoritative. Doing this from `onSettled` instead of a mount-only
 *   effect ensures the payload includes the hydrated `columnOrder`
 *   rather than the pre-hydration default.
 *
 * ### Versioning
 *
 * `PROSPECT_VIEW_STATE_VERSION` gates server hydration. Bump it whenever
 * {@link ProspectViewStatePayload} changes incompatibly so older saved
 * blobs are ignored rather than mis-parsed.
 *
 * @param fieldDefinitions - Column definitions for the prospect table.
 *   Used to validate and reconcile saved column order against the
 *   currently available columns (unknown ids are dropped, missing ids
 *   are appended).
 *
 * @returns Stable callbacks and current values for the prospect table's
 *   filter, sort, visibility, and column-order state.
 */
export default function useProspectFilterParams(
  fieldDefinitions: ProspectTableFieldDefinition[],
): ProspectFilterParams {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // `pendingParamsRef` holds the URLSearchParams we just navigated to
  // until the router state catches up. It's reset to `null` in an effect
  // when the router-supplied `searchParams` changes.
  const pendingParamsRef = useRef<URLSearchParams | null>(null);
  useEffect(() => {
    pendingParamsRef.current = null;
  }, [searchParams]);

  // Snapshot of the URL at mount, used by hydration / URL-wins push.
  // `useState` lazy init guarantees we capture render 1's value once.
  const [mountSearchParams] = useState(() => new URLSearchParams(searchParams));

  // Refs that mirror identity-volatile values so the callbacks below can
  // stay stable across re-renders. Each mirror is written from a
  // dependency-less effect so the writes happen after commit rather than
  // during render.
  const searchParamsRef = useRef(searchParams);
  const locationRef = useRef(location);
  const fieldDefinitionsRef = useRef(fieldDefinitions);
  useEffect(() => {
    searchParamsRef.current = searchParams;
    locationRef.current = location;
    fieldDefinitionsRef.current = fieldDefinitions;
  });

  const readLatestParams = useCallback(
    () => pendingParamsRef.current ?? searchParamsRef.current,
    [],
  );

  const availableColumnIds = useMemo(
    () => fieldDefinitions.map((field) => field.field),
    [fieldDefinitions],
  );

  // Late-bound forward reference so `useReconciledColumnOrder` can fire
  // its `onChange` callback without depending on `syncCurrent`, which
  // is defined further down (and itself reads `columnOrderRef` from the
  // hook below — circular at definition time, fine at call time).
  const syncCurrentRef = useRef<() => void>(() => {});
  const triggerSync = useCallback(() => syncCurrentRef.current(), []);

  const { columnOrder, setColumnOrder, hydrateColumnOrder, columnOrderRef } =
    useReconciledColumnOrder(availableColumnIds, { onChange: triggerSync });

  const syncRef = useRef<(state: ProspectViewStatePayload) => void>(() => {});

  /**
   * Single funnel for every server-sync call. All four trigger sites
   * (URL change, user reorder, post-hydrate URL merge, mount-time
   * URL-wins push) read from the same canonical refs — `readLatestParams`
   * for URL, `columnOrderRef` for order — so the payload can never drift
   * between callers, and adding a future invariant means changing one
   * place.
   */
  const syncCurrent = useCallback(() => {
    syncRef.current({
      params: extractViewStateParams(readLatestParams()),
      columnOrder: columnOrderRef.current,
    });
  }, [readLatestParams, columnOrderRef]);
  useEffect(() => {
    syncCurrentRef.current = syncCurrent;
  });

  const replaceParams = useCallback(
    (
      nextParams: URLSearchParams,
      options?: { sync?: boolean; flush?: boolean },
    ) => {
      const nextSearch = writeReadableParams(nextParams);
      pendingParamsRef.current = new URLSearchParams(nextSearch);
      const commit = () => {
        navigate(
          {
            pathname: locationRef.current.pathname,
            search: nextSearch ? `?${nextSearch}` : "",
          },
          { replace: true },
        );
      };
      // `flushSync` forces the URL state update to commit synchronously.
      // React Router 7 wraps `navigate` in `startTransition`, making it
      // interruptible. When a user types into a filter input, a fast
      // second keystroke can interrupt the in-flight transition; when the
      // transition later resumes, MRT's filter-input sync effect runs
      // with the stale parsed value and overwrites the freshly-typed
      // character. Committing synchronously closes that window.
      //
      // Hydration opts out (`flush: false`): it runs inside an effect,
      // where React disallows `flushSync`, and the change isn't
      // user-driven so there's no keystroke race to close.
      if (options?.flush === false) {
        commit();
      } else {
        flushSync(commit);
      }
      if (options?.sync !== false) syncCurrent();
    },
    [navigate, syncCurrent],
  );

  const patchParams = useCallback(
    (
      apply: (params: URLSearchParams) => void,
      options?: { sync?: boolean; flush?: boolean; resetPage?: boolean },
    ) => {
      const nextParams = buildNextParams(readLatestParams(), apply);
      if (nextParams === null) return;
      if (options?.resetPage) writePageIndex(nextParams, 0);
      replaceParams(nextParams, options);
    },
    [readLatestParams, replaceParams],
  );

  const onHydrate = useCallback(
    (state: ProspectViewStatePayload) => {
      if (!state || typeof state !== "object") return;

      if (isStringArray(state.columnOrder)) {
        hydrateColumnOrder(state.columnOrder);
      }

      const incoming = state.params;
      if (!incoming || typeof incoming !== "object") return;

      const merged = mergeServerParamsIntoUrl(
        incoming,
        mountSearchParams,
        readLatestParams(),
      );
      // Skip the server PUT: this URL change is just hydration echoing
      // the server's own state back. The `onSettled` URL-wins push covers
      // the case where the URL had params at mount, so suppressing here
      // can't drop a legitimate write — it just avoids a race where our
      // PUT clobbers a newer cross-device update arriving in the debounce
      // window.
      if (merged) replaceParams(merged, { sync: false, flush: false });
    },
    [hydrateColumnOrder, readLatestParams, replaceParams, mountSearchParams],
  );

  const onSettled = useCallback(() => {
    if (!hasAnyViewStateParams(mountSearchParams)) return;
    syncCurrent();
  }, [mountSearchParams, syncCurrent]);

  const { sync, hasSettled } = useStaffViewStateSync<ProspectViewStatePayload>({
    name: PROSPECT_VIEW_STATE_NAME,
    version: PROSPECT_VIEW_STATE_VERSION,
    onHydrate,
    onSettled,
  });
  useEffect(() => {
    syncRef.current = sync;
  });

  const globalFilter = searchParams.get("q") ?? "";
  const sorting = useMemo(
    () => parseSortParam(searchParams.get("sort")),
    [searchParams],
  );
  const columnFilters = useMemo(
    () => readColumnFilters(searchParams, fieldDefinitions),
    [searchParams, fieldDefinitions],
  );
  const columnVisibility = useMemo(
    () => readColumnVisibility(searchParams, fieldDefinitions),
    [searchParams, fieldDefinitions],
  );
  const showColumnFilters = readShowColumnFilters(searchParams);
  const showGlobalFilter = readShowGlobalFilter(searchParams);
  const pagination = useMemo(
    () => readPagination(searchParams),
    [searchParams],
  );

  const setColumnFilters = useCallback(
    (updater: Updater<MRT_ColumnFiltersState>) => {
      patchParams(
        (params) => {
          const fieldDefs = fieldDefinitionsRef.current;
          const nextFilters = applyUpdater(
            updater,
            readColumnFilters(params, fieldDefs),
          );
          for (const key of knownFilterKeys(params, fieldDefs)) {
            params.delete(key);
          }

          const fields = new Map(fieldDefs.map((f) => [f.field, f]));
          for (const { id, value } of nextFilters) {
            const field = fields.get(id);
            if (!field) continue;
            const raw = formatFilterValue(value, field.filterVariant);
            if (raw !== null) {
              params.set(`${FILTER_PREFIX}${id}`, raw);
            }
          }
        },
        { resetPage: true },
      );
    },
    [patchParams],
  );

  const setGlobalFilter = useCallback(
    (updater: Updater<string | undefined>) => {
      patchParams(
        (params) => {
          const next = applyUpdater(updater, params.get("q") ?? "") ?? "";
          if (next) {
            params.set("q", next);
          } else {
            params.delete("q");
          }
        },
        { resetPage: true },
      );
    },
    [patchParams],
  );

  const setColumnVisibility = useCallback(
    (updater: Updater<MRT_VisibilityState>) => {
      patchParams((params) => {
        const fieldDefs = fieldDefinitionsRef.current;
        const nextVisibility = applyUpdater(
          updater,
          readColumnVisibility(params, fieldDefs),
        );
        const hidden = hiddenColumnIds(nextVisibility, fieldDefs);
        if (hidden.length) {
          params.set("hide", hidden.join(","));
        } else {
          params.delete("hide");
        }
      });
    },
    [patchParams],
  );

  const setShowColumnFilters = useCallback(
    (updater: Updater<boolean>) => {
      patchParams((params) => {
        const next = applyUpdater(updater, readShowColumnFilters(params));
        if (next === DEFAULT_SHOW_COLUMN_FILTERS) {
          params.delete("filters");
        } else {
          params.set("filters", "off");
        }
      });
    },
    [patchParams],
  );

  const setShowGlobalFilter = useCallback(
    (updater: Updater<boolean>) => {
      patchParams((params) => {
        const next = applyUpdater(updater, readShowGlobalFilter(params));
        if (next === DEFAULT_SHOW_GLOBAL_FILTER) {
          params.delete("search");
        } else {
          params.set("search", "off");
        }
      });
    },
    [patchParams],
  );

  const setSorting = useCallback(
    (updater: Updater<MRT_SortingState>) => {
      patchParams(
        (params) => {
          const nextSorting = applyUpdater(
            updater,
            parseSortParam(params.get("sort")),
          );
          const raw = formatSortParam(nextSorting);
          if (raw === null) {
            params.delete("sort");
          } else {
            params.set("sort", raw);
          }
        },
        { resetPage: true },
      );
    },
    [patchParams],
  );

  // MRT can set the page from inside an effect, where `flushSync` isn't allowed.
  const setPagination = useCallback(
    (updater: Updater<MRT_PaginationState>) => {
      patchParams(
        (params) => {
          const next = applyUpdater(updater, readPagination(params));
          writePageIndex(params, next.pageIndex);
        },
        { sync: false, flush: false },
      );
    },
    [patchParams],
  );

  const resetFiltersAndColumns = useCallback(() => {
    patchParams(
      (params) => {
        params.delete("q");
        for (const key of knownFilterKeys(
          params,
          fieldDefinitionsRef.current,
        )) {
          params.delete(key);
        }
        params.delete("hide");
      },
      { resetPage: true },
    );
  }, [patchParams]);

  return {
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    resetFiltersAndColumns,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    showColumnFilters,
    setShowColumnFilters,
    showGlobalFilter,
    setShowGlobalFilter,
    sorting,
    setSorting,
    pagination,
    setPagination,
    hasSettled,
  };
}
